<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    severity: { high: number; medium: number; low: number };
    narrowBreakpoint?: number;
    donutSize?: number;
  }>(),
  {
    narrowBreakpoint: 890,
    donutSize: 44,
  }
);

const total = computed(() => props.severity.high + props.severity.medium + props.severity.low);

function pct(value: number) {
  if (total.value === 0) return 0;
  return (value / total.value) * 100;
}

const donutStrokeWidth = computed(() => Math.max(2, Math.round(props.donutSize / 12)));
const donutRadius = computed(() => (props.donutSize - donutStrokeWidth.value) / 2 - 2);
const donutCircum = computed(() => 2 * Math.PI * donutRadius.value);

const arcs = computed(() => {
  if (total.value === 0) {
    return {
      high: { len: 0, offset: 0 },
      medium: { len: 0, offset: 0 },
      low: { len: 0, offset: 0 },
    };
  }
  const circ = donutCircum.value;
  const high = (props.severity.high / total.value) * circ;
  const medium = (props.severity.medium / total.value) * circ;
  const low = (props.severity.low / total.value) * circ;
  return {
    high: { len: high, offset: 0 },
    medium: { len: medium, offset: -high },
    low: { len: low, offset: -(high + medium) },
  };
});

const title = computed(
  () =>
    `High: ${props.severity.high}, Medium: ${props.severity.medium}, Low: ${props.severity.low}`
);

const center = computed(() => props.donutSize / 2);
</script>

<template>
  <div :title="title">
    <div
      class="tw:flex tw:h-2 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-black/5 narrow-hidden"
    >
      <div class="tw:bg-vuln-high" :style="{ width: `${pct(severity.high)}%` }" />
      <div class="tw:bg-vuln-medium" :style="{ width: `${pct(severity.medium)}%` }" />
      <div class="tw:bg-vuln-low" :style="{ width: `${pct(severity.low)}%` }" />
    </div>

    <div
      class="narrow-visible tw:relative"
      :style="{ width: `${donutSize}px`, height: `${donutSize}px` }"
    >
      <svg
        class="tw:size-full"
        :viewBox="`0 0 ${donutSize} ${donutSize}`"
      >
        <g :transform="`rotate(-90 ${center} ${center})`">
          <circle
            :cx="center"
            :cy="center"
            :r="donutRadius"
            fill="none"
            class="tw:stroke-black/10"
            :stroke-width="donutStrokeWidth"
          />
          <circle
            :cx="center"
            :cy="center"
            :r="donutRadius"
            fill="none"
            class="tw:stroke-vuln-high tw:transition-all tw:duration-500"
            :stroke-width="donutStrokeWidth"
            stroke-linecap="round"
            :stroke-dasharray="`${arcs.high.len} ${donutCircum}`"
            :stroke-dashoffset="arcs.high.offset"
          />
          <circle
            :cx="center"
            :cy="center"
            :r="donutRadius"
            fill="none"
            class="tw:stroke-vuln-medium tw:transition-all tw:duration-500"
            :stroke-width="donutStrokeWidth"
            stroke-linecap="round"
            :stroke-dasharray="`${arcs.medium.len} ${donutCircum}`"
            :stroke-dashoffset="arcs.medium.offset"
          />
          <circle
            :cx="center"
            :cy="center"
            :r="donutRadius"
            fill="none"
            class="tw:stroke-vuln-low tw:transition-all tw:duration-500"
            :stroke-width="donutStrokeWidth"
            stroke-linecap="round"
            :stroke-dasharray="`${arcs.low.len} ${donutCircum}`"
            :stroke-dashoffset="arcs.low.offset"
          />
        </g>
      </svg>
      <div
        class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:text-xs tw:font-semibold tw:leading-none"
      >
        {{ total }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.narrow-visible {
  display: none;
}
@media not all and (min-width: 890px) {
  .narrow-hidden {
    display: none;
  }
  .narrow-visible {
    display: block;
  }
}
</style>
